import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Alert,
  Modal,
  Form,
  Tabs,
  Tab,
  Badge,
} from "react-bootstrap";
import api, { authAPI, getAuthMode } from "../services/api";
import EmployeePolls from "./EmployeePolls";
import EmployeeWorklog from "./EmployeeWorklog";
import EmployeeForm106 from "./EmployeeForm106";
import {
  isPushNotificationSupported,
  requestPushSubscription,
  isSubscribed,
} from "../services/pushNotifications";
import TwoFASetupModal from "./TwoFASetupModal";
import TwoFAButton from "./TwoFAButton";
import TwoFADisableModal from "./TwoFADisableModal";
import { useLanguage } from "../i18n/LanguageContext";
import PasswordInput from "./PasswordInput";

interface Payslip {
  id: number;
  user_id: number;
  file_name: string;
  month: string;
  year: number;
  uploaded_at: string;
}

const EmployeeDashboard: React.FC = () => {
  const { t } = useLanguage();
  const MONTHS = [
    { value: "01", label: t("january") },
    { value: "02", label: t("february") },
    { value: "03", label: t("march") },
    { value: "04", label: t("april") },
    { value: "05", label: t("may") },
    { value: "06", label: t("june") },
    { value: "07", label: t("july") },
    { value: "08", label: t("august") },
    { value: "09", label: t("september") },
    { value: "10", label: t("october") },
    { value: "11", label: t("november") },
    { value: "12", label: t("december") },
  ];
  const [payslips, setPayslips] = useState<Payslip[]>([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [unvotedPollsCount, setUnvotedPollsCount] = useState(0);
  const [notifSubscribed, setNotifSubscribed] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [showDisable2FAModal, setShowDisable2FAModal] = useState(false);
  const [authMode, setAuthMode] = useState<"password" | "authenticator">(
    "password",
  );

  useEffect(() => {
    api.get("/2fa/status").then((res) => {
      setTwoFAEnabled(res.data.enabled);
    });
    getAuthMode().then(setAuthMode);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadUserData();
    loadPayslips();
    checkUnvotedPolls();

    if (isPushNotificationSupported()) {
      // SW is already registered by App.tsx — just check subscription state
      // Use a small delay to ensure SW is fully active before checking
      setTimeout(() => {
        isSubscribed().then((subscribed) => {
          setNotifSubscribed(subscribed);
        });
      }, 1000);
    }
  }, []);

  const checkUnvotedPolls = async () => {
    try {
      const response = await api.get("/polls/active");
      const polls = response.data.polls || [];
      const unvoted = polls.filter((poll: any) => !poll.user_has_voted).length;
      setUnvotedPollsCount(unvoted);
    } catch (err) {
      // Silently fail
    }
  };

  const handleEnableNotifications = async () => {
    setNotifLoading(true);
    try {
      const subscription = await requestPushSubscription();
      if (!subscription) {
        return;
      }
      await api.post("/push/subscribe", {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(
            String.fromCharCode(
              ...Array.from(new Uint8Array(subscription.getKey("p256dh")!)),
            ),
          ),
          auth: btoa(
            String.fromCharCode(
              ...Array.from(new Uint8Array(subscription.getKey("auth")!)),
            ),
          ),
        },
      });
      setNotifSubscribed(true);
      setSuccess(t("notificationsEnabled" as any));
    } catch (err: any) {
      console.error("[Push] Subscribe failed:", err?.message || err);
      if (err?.message?.includes("VAPID")) {
        setError(
          "Push notifications are not configured yet (missing VAPID key).",
        );
      } else {
        setError("Failed to enable notifications. Please try again.");
      }
    } finally {
      setNotifLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    setNotifLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.getSubscription();
      if (subscription) {
        // Notify backend first using the app's axios instance
        await api
          .post("/push/unsubscribe", { endpoint: subscription.endpoint })
          .catch(() => {});
        // Then unsubscribe from the browser
        await subscription.unsubscribe();
      }
      setNotifSubscribed(false);
      setSuccess(t("notificationsDisabled" as any));
    } catch (err) {
      // ignore
    } finally {
      setNotifLoading(false);
    }
  };

  const loadUserData = () => {
    const userData = localStorage.getItem("user");
    if (userData) {
      setUser(JSON.parse(userData));
    }
  };

  const loadPayslips = async () => {
    try {
      setLoading(true);
      const response = await api.get("/payslips/my-payslips");
      setPayslips(response.data.payslips);
    } catch (err: any) {
      console.error("Error loading payslips:", err);
      setError(t("noPayslips"));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (payslipId: number, fileName: string) => {
    try {
      const response = await api.get(`/payslips/download/${payslipId}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError("Failed to download payslip");
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError(t("passwordsDoNotMatch"));
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setError(t("passwordTooShort"));
      return;
    }

    setLoading(true);
    try {
      await authAPI.changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword,
      );
      setSuccess(t("passwordChangedSuccess"));
      setShowChangePasswordModal(false);
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const groupedPayslips = payslips.reduce(
    (acc, payslip) => {
      if (!acc[payslip.year]) acc[payslip.year] = [];
      acc[payslip.year].push(payslip);
      return acc;
    },
    {} as Record<number, Payslip[]>,
  );

  const years = Object.keys(groupedPayslips).sort(
    (a, b) => parseInt(b) - parseInt(a),
  );

  return (
    <Container
      fluid
      className="py-3 px-3"
      style={{
        maxWidth: "100%",
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
      }}
    >
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h4 className="mb-1" style={{ fontWeight: "600" }}>
                {t("welcomeEmployee")}, {user?.full_name || t("employee")}
              </h4>
              <p className="text-muted small mb-0">
                {t("viewDownloadPayslips")}
              </p>
            </div>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowChangePasswordModal(true)}
              style={{ whiteSpace: "nowrap" }}
            >
              🔒 {t("changePassword")}
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setError("")}
          className="mb-3"
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSuccess("")}
          className="mb-3"
        >
          {success}
        </Alert>
      )}

      {/* Single notification button — shows current state, handles enable/disable */}
      {isPushNotificationSupported() && (
        <div className="mb-3 d-flex justify-content-end">
          <Button
            size="sm"
            variant={notifSubscribed ? "outline-secondary" : "outline-primary"}
            onClick={
              notifSubscribed
                ? handleDisableNotifications
                : handleEnableNotifications
            }
            disabled={notifLoading}
            style={{ fontSize: "12px" }}
          >
            {notifLoading
              ? "..."
              : notifSubscribed
                ? `🔔 ${t("notificationsOn" as any)}`
                : `🔔 ${t("notificationsOff" as any)}`}
          </Button>
        </div>
      )}

      {authMode === "password" && (
        <>
          <TwoFAButton
            enabled={twoFAEnabled}
            onClick={() => {
              if (twoFAEnabled) {
                setShowDisable2FAModal(true);
              } else {
                setShow2FAModal(true);
              }
            }}
            className="mb-3"
          />
          <TwoFASetupModal
            show={show2FAModal}
            onHide={() => setShow2FAModal(false)}
            onSuccess={() => {
              setTwoFAEnabled(true);
              setSuccess(
                "2FA enabled successfully! Make sure you saved your backup codes.",
              );
            }}
          />
          <TwoFADisableModal
            show={showDisable2FAModal}
            onHide={() => setShowDisable2FAModal(false)}
            onSuccess={() => {
              setTwoFAEnabled(false);
              setSuccess(
                "2FA has been disabled. Your account is now less secure.",
              );
            }}
          />
        </>
      )}

      <Tabs defaultActiveKey="payslips" className="mb-3">
        <Tab
          eventKey="payslips"
          title={
            <span className="small">
              📄 {t("myPayslips")}{" "}
              <Badge bg="secondary">{payslips.length}</Badge>
            </span>
          }
        >
          {loading ? (
            <Card className="shadow-sm border-0">
              <Card.Body className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">{t("loading")}</span>
                </div>
                <p className="mt-3 text-muted">{t("loading")}</p>
              </Card.Body>
            </Card>
          ) : payslips.length === 0 ? (
            <Card className="shadow-sm border-0">
              <Card.Body className="text-center py-5">
                <div className="mb-3" style={{ fontSize: "48px" }}>
                  📄
                </div>
                <h5 className="text-muted mb-2">{t("noPayslipsAvailable")}</h5>
                <p className="text-muted small px-3">
                  {t("noPayslipsMessage")}
                </p>
              </Card.Body>
            </Card>
          ) : (
            <>
              <Card className="shadow-sm border-0 mb-3">
                <Card.Body className="p-3">
                  <Row>
                    <Col xs={6} className="text-center border-end">
                      <div className="text-muted small mb-1">
                        {t("totalPayslips")}
                      </div>
                      <h3
                        className="mb-0 text-primary"
                        style={{ fontWeight: "700" }}
                      >
                        {payslips.length}
                      </h3>
                    </Col>
                    <Col xs={6} className="text-center">
                      <div className="text-muted small mb-1">
                        {t("latestPayslip")}
                      </div>
                      {payslips.length > 0 && (
                        <h5 className="mb-0" style={{ fontWeight: "600" }}>
                          {payslips[0].month} {payslips[0].year}
                        </h5>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {years.map((year) => (
                <Card key={year} className="shadow-sm border-0 mb-3">
                  <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center py-2 px-3">
                    <h5 className="mb-0" style={{ fontWeight: "600" }}>
                      {year}
                    </h5>
                    <span className="badge bg-light text-primary">
                      {groupedPayslips[parseInt(year)].length}
                    </span>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {/* Mobile */}
                    <div className="d-md-none">
                      {groupedPayslips[parseInt(year)].map((payslip) => (
                        <div
                          key={payslip.id}
                          className="p-3 border-bottom"
                          style={{ backgroundColor: "#fff" }}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "600",
                                  color: "#212529",
                                }}
                              >
                                {payslip.month}
                              </div>
                              <div className="text-muted small mt-1">
                                {new Date(
                                  payslip.uploaded_at,
                                ).toLocaleDateString()}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() =>
                                handleDownload(payslip.id, payslip.file_name)
                              }
                              style={{
                                whiteSpace: "nowrap",
                                marginLeft: "8px",
                              }}
                            >
                              📥 {t("download")}
                            </Button>
                          </div>
                          <div
                            className="small text-muted"
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {payslip.file_name}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Desktop */}
                    <div className="d-none d-md-block">
                      <Table hover className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("monthLabel")}</th>
                            <th>{t("fileName")}</th>
                            <th>{t("uploadDate")}</th>
                            <th>{t("actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedPayslips[parseInt(year)].map((payslip) => (
                            <tr key={payslip.id}>
                              <td>
                                <strong>{payslip.month}</strong>
                              </td>
                              <td>
                                <small className="text-muted">
                                  {payslip.file_name}
                                </small>
                              </td>
                              <td>
                                <small>
                                  {new Date(
                                    payslip.uploaded_at,
                                  ).toLocaleDateString()}
                                </small>
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="primary"
                                  onClick={() =>
                                    handleDownload(
                                      payslip.id,
                                      payslip.file_name,
                                    )
                                  }
                                >
                                  📥 {t("downloadPDF")}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </>
          )}
        </Tab>

        <Tab
          eventKey="polls"
          title={
            <span className="small">
              📊 {t("polls")}
              {unvotedPollsCount > 0 && (
                <Badge bg="danger" className="ms-1">
                  {unvotedPollsCount}
                </Badge>
              )}
            </span>
          }
        >
          <EmployeePolls />
        </Tab>

        <Tab
          eventKey="form106"
          title={<span className="small">📋 {t("form106Tab")}</span>}
        >
          <EmployeeForm106 />
        </Tab>

        <Tab
          eventKey="worklog"
          title={<span className="small">📋 {t("worklogTab")}</span>}
        >
          <Card>
            <Card.Body className="p-2 p-md-3">
              <h5 className="mb-3">{t("worklogTitle")}</h5>
              <EmployeeWorklog />
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      <Modal
        show={showChangePasswordModal}
        onHide={() => setShowChangePasswordModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("changePassword")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleChangePassword}>
            <PasswordInput
              value={passwordForm.currentPassword}
              onChange={(v) =>
                setPasswordForm({ ...passwordForm, currentPassword: v })
              }
              label={t("currentPassword")}
              controlId="currentPassword"
              required
            />
            <PasswordInput
              value={passwordForm.newPassword}
              onChange={(v) =>
                setPasswordForm({ ...passwordForm, newPassword: v })
              }
              label={t("newPassword")}
              controlId="newPassword"
              showStrength
              required
            />
            <div className="text-muted small mb-2 px-1">
              💡 {t("passwordComplexityHint")}
            </div>
            <PasswordInput
              value={passwordForm.confirmPassword}
              onChange={(v) =>
                setPasswordForm({ ...passwordForm, confirmPassword: v })
              }
              label={t("confirmNewPassword")}
              controlId="confirmNewPassword"
              required
            />
            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setShowChangePasswordModal(false)}
              >
                {t("cancel")}
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? t("loading") : t("changePassword")}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default EmployeeDashboard;
